'use client'

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'
import { LOGO_DONTS, type LogoDont } from './constants'
import { MaskedLogo } from './MaskedLogo'
import { useInView } from './Reveal'

// The "outline" misuse: the wordmark drawn as a true stroked outline (hollow letters).
// Stroking the real glyph paths gives an even outline that scales with the logo - far
// cleaner than faking a rim with a scale transform or drop-shadows.
const CIVO_PATHS = [
  'M74.383 7.9765C67.2661 7.9765 60.7658 13.708 60.7658 21.6972C60.7658 29.6866 67.2661 35.418 74.383 35.418C81.5557 35.418 88 29.6866 88 21.6972C88 13.708 81.5558 7.9765 74.383 7.9765ZM74.3879 32.1925C70.9697 32.1925 67.9947 28.2972 67.9947 21.6973C67.9947 15.1555 70.9659 11.1907 74.3842 11.1907C77.8586 11.1907 80.8273 15.0975 80.8273 21.6973C80.8273 28.2971 77.8623 32.1925 74.3879 32.1925Z',
  'M24.8449 0.754406C25.3686 3.46976 25.3685 6.26549 24.8447 8.98075C24.8108 9.15652 24.9875 9.29443 25.1399 9.20941L32.9271 4.86758L25.1401 0.525835C24.9877 0.440733 24.811 0.57855 24.8449 0.754406Z',
  'M35.8072 35.0804H25.1181C24.902 35.0804 24.8238 34.7795 25.0137 34.6754C26.0531 34.0969 26.8505 33.1445 27.2267 32.0067C27.3833 31.6019 27.469 31.1659 27.469 30.7147V17.7065C27.469 16.4956 26.9287 15.3464 26.0047 14.5981L24.7221 13.5586C24.6066 13.465 24.6219 13.2795 24.751 13.2074L29.4511 10.5872L33.1602 8.52122C33.2957 8.44573 33.46 8.54737 33.46 8.70661V30.5181C33.46 31.0619 33.5532 31.5863 33.7245 32.0722C34.1082 33.1791 34.8906 34.1046 35.9039 34.6716C36.0977 34.7795 36.0269 35.0804 35.8072 35.0804Z',
  'M62.0444 8.55482L61.493 8.55103H53.3635C53.1361 8.55103 53.0728 8.86723 53.2778 8.9676C53.7546 9.20654 54.1681 9.51128 54.5147 9.86223C55.9491 11.3161 56.2286 13.6032 55.1556 15.6279L55.1444 15.6511C55.1146 15.7243 54.9022 16.2024 54.5781 16.9159C53.5759 19.1295 51.5268 23.611 50.6624 25.4929L45.722 14.7525C44.8613 12.5965 45.8636 10.0782 47.7637 8.95984C47.9538 8.848 47.8792 8.55103 47.6631 8.55103H36.1804C36.0276 8.55103 35.9233 8.71676 35.9903 8.86343L47.9908 34.9569C48.0281 35.0304 48.0988 35.0805 48.1809 35.0805H50.2226C50.3008 35.0805 50.3716 35.0342 50.4053 34.9609C50.9901 33.6689 51.7837 31.918 52.6927 29.9125C53.2814 28.6129 53.9185 27.2091 54.5779 25.7514C57.3946 19.5423 60.6173 12.4345 62.2344 8.86343C62.3014 8.72073 62.1972 8.55482 62.0444 8.55482Z',
  'M0.00625797 22.1476C-0.0330976 20.6355 -0.013705 15.4606 4.14234 11.4713C6.99134 8.73656 10.3347 8.2068 11.5919 8.0204C16.447 7.3007 20.2344 9.42699 21.6882 10.3403C21.8717 10.4557 21.9825 10.6623 21.9825 10.8845V17.4846C21.9825 17.6938 21.7207 17.7734 21.609 17.5991C20.4202 15.7447 19.1221 14.4153 17.6369 13.6106C16.4984 12.9937 15.2501 12.6853 13.857 12.6853C9.84498 12.6853 6.31007 15.6291 6.31007 20.518C6.31007 25.8799 10.2512 29.402 15.482 29.402C18.0325 29.402 20.4984 28.4817 22.1061 26.5607C22.2386 26.4023 22.4907 26.5172 22.4643 26.725C21.7316 32.4975 18.0871 35.5 12.3841 35.5C4.9697 35.5 0.215584 30.1876 0.00625797 22.1476Z',
]

// Paths use pathLength=1 + stroke-dasharray:1 so they sit solid at rest and can "draw" on hover.
const OutlineWordmark: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 88 36"
    fill="none"
    stroke="#FFFFFF"
    strokeWidth={1.4}
    strokeLinejoin="round"
    className={cn('dont-outline [stroke-dasharray:1]', className)}
  >
    {CIVO_PATHS.map((d, i) => (
      <path key={i} d={d} pathLength={1} />
    ))}
  </svg>
)

// Each misuse is illustrated by distorting the masked wordmark.
function treatmentProps(t: LogoDont['treatment']): {
  color: string
  style?: React.CSSProperties
} {
  switch (t) {
    case 'stretch':
      return { color: '#FFFFFF', style: { transform: 'scaleX(1.7)' } }
    case 'outline':
      // Rendered via OutlineWordmark (stroked); colour here is unused.
      return { color: '#FFFFFF' }
    case 'recolour':
      return { color: '#2f56d6' }
    case 'lowContrast':
      return { color: '#c2bba9' }
    case 'rotate':
      return { color: '#FFFFFF', style: { transform: 'rotate(-11deg)' } }
    case 'effects':
      return { color: '#FFFFFF', style: { filter: 'drop-shadow(2px 4px 3px rgba(0,0,0,0.45))' } }
  }
}

const DontCard: React.FC<{ item: LogoDont; index: number }> = ({ item, index }) => {
  const { ref, inView } = useInView<HTMLDivElement>(0.3)
  const { color, style } = treatmentProps(item.treatment)

  // Hover plays the replay on pointer devices; tap plays it on touch (toggles .dont-playing,
  // cleared after the sequence so it can replay on the next tap).
  const [playing, setPlaying] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const play = () => {
    if (timer.current) clearTimeout(timer.current)
    setPlaying(true)
    timer.current = setTimeout(() => setPlaying(false), 1600)
  }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  return (
    <div
      ref={ref}
      onClick={play}
      style={{ transitionDelay: inView ? `${Math.min(index, 5) * 90}ms` : '0ms' }}
      className={cn(
        'group flex cursor-pointer flex-col gap-3.5 transition-[opacity,transform] duration-500 ease-out sm:flex-row sm:items-center sm:gap-5',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        inView ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
        playing && 'dont-playing',
      )}
    >
      {/* Mobile: full-width box, caption stacked below. sm+: compact box, caption beside. */}
      <div className="relative flex h-[120px] w-full shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-civo-cloud sm:h-[78px] sm:w-[120px] sm:rounded-[8px] lg:h-[156px] lg:w-[240px] lg:rounded-[16px]">
        {item.treatment === 'outline' ? (
          // True stroked outline; draws in on hover.
          <OutlineWordmark className="h-[48px] w-[160px] sm:h-[26px] sm:w-[78px] lg:h-[52px] lg:w-[156px]" />
        ) : item.treatment === 'effects' ? (
          // Shadow lives on a wrapper - a filter on the masked logo gets clipped by the mask.
          <span
            className="dont-replay-effects inline-block h-[48px] w-[160px] sm:h-[26px] sm:w-[78px] lg:h-[52px] lg:w-[156px]"
            style={style}
          >
            <MaskedLogo color={color} className="block h-full w-full" />
          </span>
        ) : (
          // Rests on the misuse; on desktop hover it replays from the normal mark (dont-replay-*).
          <MaskedLogo
            color={color}
            className={cn(
              `dont-replay-${item.treatment}`,
              'h-[48px] w-[160px] sm:h-[26px] sm:w-[78px] lg:h-[52px] lg:w-[156px]',
            )}
            style={style}
          />
        )}
        {/* Red strike: an SVG diagonal corner-to-corner (exact at any box ratio), growing
            from the centre on reveal and redrawing on desktop hover (dont-strike). */}
        <svg
          aria-hidden="true"
          preserveAspectRatio="none"
          className={cn(
            'absolute inset-0 h-full w-full origin-center transition-transform duration-500 ease-out',
            'motion-reduce:transition-none motion-reduce:scale-100',
            inView ? 'scale-100' : 'scale-0',
          )}
          style={{ transitionDelay: inView ? `${Math.min(index, 5) * 90 + 250}ms` : '0ms' }}
        >
          <line
            x1="0"
            y1="100%"
            x2="100%"
            y2="0"
            pathLength={1}
            className="dont-strike stroke-red-500 stroke-[2px] [stroke-dasharray:1] lg:stroke-[3px]"
          />
        </svg>
      </div>
      {/* Caption nudges right while the replay plays (hover or tap) */}
      <div className="dont-caption flex flex-col gap-1 transition-transform duration-300 ease-out motion-reduce:transition-none">
        <h3 className="subheading-small text-civo-black">{item.title}</h3>
        <p className="body-small text-neutral-500">{item.why}</p>
      </div>
    </div>
  )
}

export const LogoDonts: React.FC = () => (
  <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-12 sm:gap-y-9">
    {LOGO_DONTS.map((item, i) => (
      <DontCard key={item.title} item={item} index={i} />
    ))}
  </div>
)
